import React, { LegacyRef, forwardRef } from "react";

import { styled } from "@mui/material/styles";
import Button from "@mui/material/Button";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import DeleteIcon from "@mui/icons-material/Delete";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import ToggleButton from "@mui/material/ToggleButton";
import Autocomplete from "@mui/material/Autocomplete";
import DragIndicatorOutlinedIcon from "@mui/icons-material/DragIndicatorOutlined";
import { useDrag } from "react-dnd";

import { draggable_types, ConditionDragItem } from "../../helpers/DragHelper";
import Condition, { enum_conditional } from "../../models/conditions/Condition";
import { anchorKeys } from "../../models/anchors/AnchorProperty";
import { useRowSelector } from "../../store/Hooks";

import { ConditionProps } from "./ConditionComponent";

const RuleContainer = styled("div")(({ theme }) => ({
  display: "flex",
}));

const RuleRow = styled("div")(({ theme }) => ({
  background: "white",
  borderRadius: 5,
}));

const RuleFields = styled("div")(({ theme }) => ({
  display: "flex",
  marginLeft: "22px",
  flexGrow: 1,
}));

const RuleDetails = styled("div")(({ theme }) => ({
  display: "flex",
  flexGrow: 1,
  flexWrap: "wrap",
}));

const DeleteRule = styled(Button)(({ theme }) => ({
  float: "right",
}));

const InputContainer = styled(FormControl)(({ theme }) => ({
  margin: 5,
}));

const NotToggle = styled(ToggleButton)(({ theme }) => ({
  "&.Mui-selected, &.Mui-selected:hover": {
    color: "white",
    backgroundColor: theme.palette.secondary.main,
  },
  margin: "5px 5px 5px 5px",
}));

const DragHandle = styled(DragIndicatorOutlinedIcon)(({ theme }) => ({
  cursor: "grab",
  margin: "auto",
  position: "absolute",
  top: "calc(50% - 11px)",
}));

const RuleValueContainer = styled(FormControl)(({ theme }) => ({
  margin: 5,
}));

const OperatorContainer = styled(FormControl)(({ theme }) => ({
  margin: 5,
}));

const ConditionRule = forwardRef(
  (props: ConditionProps, ref: LegacyRef<HTMLDivElement> | undefined) => {
    const condition = useRowSelector(Condition, props.condition_id);
    if (typeof condition === "undefined") {
      throw new Error("Unable to display a condition that does not exist");
    }
    const parent = useRowSelector(Condition, condition.parent_condition_id as number);
    const draggable = condition.parent_condition_id !== null;
    const readonly = props.readonly;

    const [{ isDragging }, drag, preview] = useDrag<
      ConditionDragItem,
      void,
      { isDragging: boolean | undefined }
    >(
      () => ({
        type: draggable_types.Condition,
        canDrag: () => draggable,
        item: new ConditionDragItem(
          condition.id,
          props.index,
          condition.parent_condition_id,
        ),
        collect: (monitor) => ({
          isDragging: monitor.isDragging(),
        }),
      }),
      [condition],
    );

    let extraFields = null;
    let ruleKeyValues = null;
    if (condition.conditional === enum_conditional.v1_data) {
      extraFields = (
        <>
          <InputContainer>
            <TextField
              name="from"
              label="Table"
              value={condition.from === null ? "" : condition.from}
              sx={{ maxWidth: "130px" }}
              onChange={(event: React.ChangeEvent<any>) => {
                if (!readonly) {
                  condition.update("from", event.target.value);
                }
              }}
              disabled={readonly}
            ></TextField>
          </InputContainer>
          <InputContainer>
            <TextField
              name="as"
              label="Query Alias"
              value={condition.as === null ? "" : condition.as}
              sx={{ maxWidth: "130px" }}
              onChange={(event: React.ChangeEvent<any>) => {
                if (!readonly) {
                  condition.update("as", event.target.value);
                }
              }}
              disabled={readonly}
            ></TextField>
          </InputContainer>
        </>
      );
    }
    if (condition.conditional !== enum_conditional.else) {
      const autoCompleteOptions =
        condition.conditional === enum_conditional.anchor_property
          ? anchorKeys
          : [];
      autoCompleteOptions.sort();
      ruleKeyValues = (
        <>
          <InputContainer>
            <Autocomplete
              value={condition.key === null ? "" : condition.key}
              freeSolo
              disabled={readonly}
              options={autoCompleteOptions}
              sx={{ width: "130px" }}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  (event as any).defaultMuiPrevented = true;
                }
              }}
              onInputChange={(
                event: React.SyntheticEvent,
                value: string | null,
              ) => {
                if (!readonly) {
                  condition.update("key", value === null ? "" : value);
                }
              }}
              renderInput={(params) => (
                <TextField {...params} name="key" label="Key"></TextField>
              )}
            />
          </InputContainer>
          <OperatorContainer>
            <Select disabled={true} value="select_equals">
              <MenuItem value="select_equals">==</MenuItem>
            </Select>
          </OperatorContainer>
          <RuleValueContainer>
            <TextField
              label="Value"
              name="value"
              value={condition.value === null ? "" : condition.value}
              onChange={(event: React.ChangeEvent<any>) => {
                if (!readonly) {
                  condition.update("value", event.target.value);
                }
              }}
              disabled={readonly}
            ></TextField>
          </RuleValueContainer>
        </>
      );
    }
    return (
      <RuleContainer ref={ref} sx={props.sx}>
        <RuleRow ref={preview}>
          <span ref={drag}>
            <DragHandle />
          </span>
          <RuleFields>
            <NotToggle
              color="secondary"
              value="NOT"
              selected={condition.not}
              onChange={() => {
                if (!readonly) {
                  condition.update("not", !condition.not);
                }
              }}
              disabled={readonly}
            >
              NOT
            </NotToggle>
            <RuleDetails>
              <InputContainer>
                <InputLabel id={condition.id + "-condition-select-label"}>
                  Conditional
                </InputLabel>
                <Select
                  labelId={condition.id + "-key-select-label"}
                  label="Conditional"
                  name="conditional"
                  value={condition.conditional}
                  onChange={(event: SelectChangeEvent<any>) => {
                    if (!readonly) {
                      condition.update("conditional", event.target.value);
                    }
                  }}
                  disabled={readonly}
                >
                  {Array.from(Object.values(enum_conditional))
                    .filter(
                      (el) => el !== "and" && el !== "or" && el !== "condition",
                    )
                    .map((el, index) => (
                      <MenuItem key={index} value={el.toString()}>
                        {el}
                      </MenuItem>
                    ))}
                </Select>
              </InputContainer>
              {extraFields}
              {ruleKeyValues}
            </RuleDetails>
            <DeleteRule
              onClick={() => {
                if (!readonly) {
                  if (typeof parent !== "undefined") {
                    parent.removeChild(condition as Condition);
                  } else {
                    throw new Error(
                      "Unable to delete Condition rule without a parent",
                    );
                  }
                }
              }}
              disabled={readonly}
            >
              <DeleteIcon />
            </DeleteRule>
          </RuleFields>
        </RuleRow>
      </RuleContainer>
    );
  },
);
ConditionRule.displayName = "ConditionRule";

export default ConditionRule;
